// ═══════════════════════════════════════════════════════════════════════════
//  PEMBACA MIDI → MELODI ACUAN
//
//  Tanpa berkas ini, kr_song_melodi praktis tidak akan pernah terisi. Satu
//  lagu punya 300–800 nada; tidak ada yang akan mengetikkannya sebagai JSON,
//  dan kolom yang tidak pernah terisi sama saja dengan fitur yang tidak ada.
//
//  Yang dibaca cuma yang dibutuhkan: Note On / Note Off, tempo, dan division.
//  Bukan pustaka MIDI — pemilih program, pitch bend, dan SysEx dilewati
//  panjangnya saja.
//
//  ── SATU LAGU PUNYA BANYAK TRACK, DAN CUMA SATU YANG BENAR ────────────────
//
//  Berkas MIDI karaoke berisi bass, drum, string, DAN melodi vokal.
//  Menggabung semuanya menghasilkan "melodi" berjangkauan lima oktaf yang
//  membuat setiap penyanyi dinilai terhadap garis bass. Skornya akan rendah
//  merata, tanpa pola, dan yang menyelidikinya akan mencurigai mikrofon.
//
//  Jadi pembaca ini TIDAK menggabungkan. Ia mengembalikan tiap track apa
//  adanya beserta namanya, dan orangnya yang memilih.
// ═══════════════════════════════════════════════════════════════════════════

export type NadaMidi = { t: number; d: number; m: number }

export type TrackMidi = {
  indeks: number
  nama: string
  nada: NadaMidi[]
  terendah: number
  tertinggi: number
  /** Dugaan: track ini melodi vokal atau bukan. Dugaan, bukan keputusan. */
  mungkinVokal: boolean
  alasan: string
}

class Pembaca {
  private d: DataView
  // Publik: pembacaan track butuh menandai batas akhirnya dan melompat ke
  // sana. Menyembunyikannya cuma memaksa cast `as unknown as {p:number}` di
  // empat tempat — pagar yang dilangkahi bukan pagar.
  p = 0
  constructor(buf: ArrayBuffer) { this.d = new DataView(buf) }
  get sisa() { return this.d.byteLength - this.p }
  u8() { return this.d.getUint8(this.p++) }
  u16() { const v = this.d.getUint16(this.p); this.p += 2; return v }
  u32() { const v = this.d.getUint32(this.p); this.p += 4; return v }
  teks(n: number) {
    let s = ''
    for (let i = 0; i < n; i++) s += String.fromCharCode(this.u8())
    return s
  }
  lompat(n: number) { this.p += n }
  /** Variable-length quantity — cara MIDI menyimpan waktu delta. */
  vlq() {
    let v = 0
    for (let i = 0; i < 4; i++) {
      const b = this.u8()
      v = (v << 7) | (b & 0x7f)
      if ((b & 0x80) === 0) break
    }
    return v
  }
}

export function bacaMidi(buf: ArrayBuffer): TrackMidi[] {
  const r = new Pembaca(buf)
  if (r.teks(4) !== 'MThd') throw new Error('Bukan berkas MIDI (header MThd tidak ada)')
  const panjangHeader = r.u32()
  r.u16() // format
  const jmlTrack = r.u16()
  const division = r.u16()
  r.lompat(panjangHeader - 6)

  if (division & 0x8000) {
    // SMPTE timecode. Jarang sekali pada MIDI musik, dan menanganinya salah
    // menghasilkan waktu yang meleset TANPA galat — semua nada tergeser
    // seragam, yang terlihat seperti penyanyi yang selalu telat.
    throw new Error('Berkas MIDI ini memakai satuan waktu SMPTE, yang belum didukung.')
  }
  const tpqn = division || 480

  const hasil: TrackMidi[] = []

  for (let ti = 0; ti < jmlTrack && r.sisa > 8; ti++) {
    if (r.teks(4) !== 'MTrk') break
    const panjang = r.u32()
    const akhirTrack = r.p + panjang

    let tick = 0
    let statusTerakhir = 0
    let nama = ''
    // Tempo bisa BERUBAH di tengah lagu. Menganggapnya tetap membuat separuh
    // kedua lagu tergeser — dan geserannya bertambah terus, jadi bagian akhir
    // meleset paling jauh. Karena itu waktu dijumlahkan sepotong demi
    // sepotong, bukan dihitung sekali di akhir.
    let usPerBeat = 500000 // 120 bpm, bawaan MIDI
    let detik = 0
    let tickTerakhir = 0

    const nyala = new Map<number, { mulai: number }>()
    const nada: NadaMidi[] = []

    const majuKe = (t: number) => {
      detik += ((t - tickTerakhir) / tpqn) * (usPerBeat / 1e6)
      tickTerakhir = t
    }

    while (r.p < akhirTrack) {
      tick += r.vlq()
      let status = r.u8()
      if (status < 0x80) {
        // Running status: pesan tanpa byte status, memakai status sebelumnya.
        // Melewatkannya membuat pembacaan bergeser satu byte dan seluruh sisa
        // track menjadi sampah — biasanya muncul sebagai "nada" bernomor
        // ratusan.
        r.p--
        status = statusTerakhir
      } else if (status < 0xf0) {
        statusTerakhir = status
      }

      const tipe = status & 0xf0

      if (status === 0xff) {
        const meta = r.u8()
        const n = r.vlq()
        if (meta === 0x03 && !nama) nama = r.teks(n)
        else if (meta === 0x51 && n === 3) {
          majuKe(tick)
          usPerBeat = (r.u8() << 16) | (r.u8() << 8) | r.u8()
        } else r.lompat(n)
        continue
      }
      if (status === 0xf0 || status === 0xf7) { r.lompat(r.vlq()); continue }

      if (tipe === 0x90 || tipe === 0x80) {
        const not = r.u8()
        const vel = r.u8()
        majuKe(tick)
        if (tipe === 0x90 && vel > 0) {
          nyala.set(not, { mulai: detik })
        } else {
          const a = nyala.get(not)
          if (a) {
            nyala.delete(not)
            const d = detik - a.mulai
            // Nada sepanjang nol tidak akan pernah cocok dengan apa pun —
            // pemutar mencari nada yang aktif PADA suatu detik, dan rentang
            // kosong tidak pernah aktif. Dibuang di sini, bukan dibiarkan
            // ditolak server nanti dengan pesan yang menyebut "nada ke-137".
            if (d > 0.02) nada.push({ t: +a.mulai.toFixed(3), d: +d.toFixed(3), m: not })
          }
        }
        continue
      }

      // Sisanya: panjangnya yang penting, isinya tidak.
      switch (tipe) {
        case 0xa0: case 0xb0: case 0xe0: r.lompat(2); break
        case 0xc0: case 0xd0: r.lompat(1); break
        default: r.lompat(1)
      }
    }
    r.p = akhirTrack

    if (nada.length === 0) continue
    nada.sort((a, b) => a.t - b.t)
    const not = nada.map(n => n.m)
    const terendah = Math.min(...not)
    const tertinggi = Math.max(...not)
    const jangkauan = tertinggi - terendah

    // ── DUGAAN "INI JALUR VOKAL" ────────────────────────────────────────────
    //
    // Disebut DUGAAN di layar, bukan pilihan otomatis. Tiga tanda:
    //   · jangkauannya masuk akal untuk satu suara manusia (≤ 2 oktaf)
    //   · nadanya di rentang suara orang, bukan bass atau string tinggi
    //   · sedikit sekali nada yang bertumpuk — orang menyanyi satu nada
    //     pada satu waktu, iringan tidak
    let tumpang = 0
    for (let i = 1; i < nada.length; i++) {
      if (nada[i].t < nada[i - 1].t + nada[i - 1].d - 0.02) tumpang++
    }
    const rasioTumpang = tumpang / nada.length
    const rerata = not.reduce((a, b) => a + b, 0) / not.length

    const alasan: string[] = []
    if (jangkauan > 24) alasan.push(`jangkauan ${jangkauan} semitone`)
    if (rerata < 48) alasan.push('rata-rata nadanya rendah — mungkin bass')
    if (rasioTumpang > 0.15) alasan.push(`${Math.round(rasioTumpang * 100)}% nada bertumpuk — mungkin akor`)

    hasil.push({
      indeks: ti,
      nama: nama.trim() || `Track ${ti + 1}`,
      nada, terendah, tertinggi,
      mungkinVokal: alasan.length === 0,
      alasan: alasan.join(' · '),
    })
  }

  if (hasil.length === 0) throw new Error('Tidak ada satu pun nada di berkas MIDI ini.')
  return hasil
}

const NAMA = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
export const namaNot = (m: number) => `${NAMA[m % 12]}${Math.floor(m / 12) - 1}`
